// ════════════════════════════════════════════════════════════════════════
//  Die Datenbankseite des Übertragens
//
//  Der Export ist eine MUTATION und keine Abfrage. Er lädt den Inhalt jeder
//  veröffentlichten Fassung – das ist viel, und es soll nur passieren, wenn
//  jemand den Knopf drückt, nicht bei jedem Öffnen der Lehrgangsliste.
//
//  Geschrieben wird in ZWEI DURCHGÄNGEN: erst die Lehrgänge samt Entwurf,
//  danach `requires_id`. Anders hinge die Reihenfolge an der Kette der
//  Abhängigkeiten, und der Zyklus-Trigger sähe eine halb angelegte Kette.
// ════════════════════════════════════════════════════════════════════════

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { parseShape } from "@otto/lehrgang";
import type { Json, OttoClient } from "@otto/plattform";
import { keysOf } from "../../../shared/lib/contentKeys.js";
import { CURRICULA_KEY } from "../../curricula/lib/data.js";
import { VERSIONS_KEY } from "../../versions/lib/data.js";
import { buildTransfer, type ExportSource, type TransferFile } from "./transferFile.js";
import { requiresIdOf, type ImportEntry, type LiveCurriculum } from "./importPlan.js";

/** Was eine Abfrage über `curricula` samt ihren Fassungen zurückgibt. */
type Row = {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  position: number;
  requires_id: string | null;
  retired_at: string | null;
  curriculum_versions: {
    version: number;
    published_at: string | null;
    content: unknown;
  }[];
};

const SELECT =
  "id, slug, title, subtitle, position, requires_id, retired_at, " +
  "curriculum_versions(version, published_at, content)";

async function allRows(client: OttoClient): Promise<Row[]> {
  const { data, error } = await client.from("curricula").select(SELECT).order("position");
  if (error) throw error;
  return (data ?? []) as unknown as Row[];
}

/** Die neueste veröffentlichte Fassung einer Zeile, oder null. */
function latestPublished(row: Row) {
  const published = row.curriculum_versions
    .filter((v) => v.published_at !== null)
    .sort((a, b) => b.version - a.version);
  return published[0] ?? null;
}

export type ExportResult = {
  readonly file: TransferFile;
  /** Lehrgänge, die nicht in die Datei kommen, mit Grund – ehrlich benannt. */
  readonly skipped: readonly { readonly title: string; readonly reason: string }[];
};

/**
 * Der Export.
 *
 * **Stillgelegte gehen nicht mit.** Ein stillgelegter Lehrgang bekommt keine
 * neuen Zuordnungen mehr; ihn in eine Umgebung zu tragen, in der er nie war,
 * hieße einen Lehrgang anzulegen, den niemand benutzen darf.
 *
 * Mit geht ausschließlich die **zuletzt veröffentlichte Fassung**. Ein Entwurf
 * ist ein laufender Vorgang; was weitergereicht wird, hat hier einmal die
 * Prüfung passiert. Die dreißig Zwischenstände bleiben, wo sie entstanden sind.
 */
export function useExportCurricula(client: OttoClient) {
  return useMutation({
    mutationFn: async (source: string): Promise<ExportResult> => {
      const rows = await allRows(client);
      // Auch die stillgelegten: `requires_id` darf auf einen zeigen, und der
      // Slug ist dann trotzdem die richtige Antwort.
      const slugById = new Map(rows.map((r) => [r.id, r.slug]));

      const curricula: ExportSource[] = [];
      const skipped: { title: string; reason: string }[] = [];

      for (const row of rows) {
        if (row.retired_at !== null) {
          skipped.push({ title: row.title, reason: "stillgelegt" });
          continue;
        }
        const version = latestPublished(row);
        if (!version) {
          skipped.push({ title: row.title, reason: "noch nie veröffentlicht" });
          continue;
        }
        const checked = parseShape(version.content);
        if ("problems" in checked) {
          // Sollte nicht vorkommen – veröffentlicht wird nur, was durch die
          // Prüfung ging. Stillschweigend weglassen wäre trotzdem falsch: die
          // Datei wäre dann unvollständig, ohne dass jemand es erführe.
          skipped.push({
            title: row.title,
            reason: `Fassung ${version.version} ist nicht lesbar`,
          });
          continue;
        }
        curricula.push({
          slug: row.slug,
          title: row.title,
          subtitle: row.subtitle,
          position: row.position,
          requiresSlug: row.requires_id ? (slugById.get(row.requires_id) ?? null) : null,
          version: version.version,
          publishedAt: version.published_at ?? "",
          bundle: checked.bundle,
        });
      }

      return {
        file: buildTransfer({ source, exportedAt: new Date(), curricula }),
        skipped,
      };
    },
  });
}

/**
 * Der Zustand DIESER Umgebung, wie ihn `planImport` braucht.
 *
 * Auch die stillgelegten Lehrgänge: ihr Slug ist vergeben, und an ihren
 * Schlüsseln hängt Fortschritt. Sie aus der Prüfung zu lassen hieße, genau die
 * Kollision durchzuwinken, die still den Fortschritt der Kinder teilt.
 */
export function useLiveCurricula(client: OttoClient, enabled: boolean) {
  return useQuery({
    queryKey: ["transfer", "live"],
    enabled,
    queryFn: async (): Promise<LiveCurriculum[]> => {
      const rows = await allRows(client);
      return rows.map((row) => {
        const keys = new Set<string>();
        for (const version of row.curriculum_versions) {
          if (version.published_at === null) continue;
          const checked = parseShape(version.content);
          if ("bundle" in checked)
            for (const key of keysOf(checked.bundle)) keys.add(key);
        }
        const latest = latestPublished(row);
        const published = latest ? parseShape(latest.content) : null;
        const versions = row.curriculum_versions.map((v) => v.version);
        return {
          id: row.id,
          slug: row.slug,
          hasDraft: row.curriculum_versions.some((v) => v.published_at === null),
          nextVersion: versions.length > 0 ? Math.max(...versions) + 1 : 1,
          keys,
          publishedBundle: published && "bundle" in published ? published.bundle : null,
          publishedVersion: latest?.version ?? null,
        };
      });
    },
  });
}

export type ImportReport = {
  readonly slug: string;
  readonly title: string;
  readonly ok: boolean;
  readonly version: number;
  readonly message: string;
};

/**
 * Schreiben – zwei Durchgänge, und je Lehrgang ganz oder gar nicht.
 *
 * Über PostgREST gibt es keine Transaktion über mehrere Aufrufe. Ein Teilerfolg
 * ist deshalb möglich und wird berichtet, statt als „ging schief“ verschwiegen
 * zu werden. Scheitert der Entwurf eines frisch angelegten Lehrgangs, wird der
 * Lehrgang wieder gelöscht – genau wie in `useCreateCurriculum`; ein Name ohne
 * Fassung ist nichts, woran jemand arbeiten könnte.
 */
export function useImportCurricula(client: OttoClient) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      readonly entries: readonly ImportEntry[];
      readonly live: readonly LiveCurriculum[];
    }): Promise<readonly ImportReport[]> => {
      const reports: ImportReport[] = [];
      // Slug → id, gefüllt im ersten Durchgang. Vorhandene stehen von Anfang an
      // darin: der Vorgänger muss nicht mitimportiert werden, um auflösbar zu sein.
      const idBySlug = new Map(input.live.map((c) => [c.slug, c.id]));
      const written: ImportEntry[] = [];

      // ── Erster Durchgang: Lehrgang und Entwurf ──────────────────────────
      for (const entry of input.entries) {
        if (entry.bundle === null) continue;
        const target = input.live.find((c) => c.slug === entry.slug) ?? null;
        let createdId: string | null = null;

        try {
          if (target === null) {
            const { data, error } = await client
              .from("curricula")
              .insert({
                slug: entry.slug,
                title: entry.title,
                subtitle: entry.subtitle,
                position: entry.position,
              })
              .select("id")
              .single();
            if (error) throw error;
            createdId = data.id;
            idBySlug.set(entry.slug, data.id);
          } else {
            // Stammdaten werden auch bei einem vorhandenen Lehrgang nachgezogen:
            // die Redaktion arbeitet in der Quellumgebung, das ist der Zweck der
            // Übung. `retired_at` bleibt unangetastet – Stilllegen ist eine
            // Entscheidung DIESER Umgebung, kein Inhalt. Und der Slug erst recht
            // nicht: er ist die Identität.
            const { error } = await client
              .from("curricula")
              .update({
                title: entry.title,
                subtitle: entry.subtitle,
                position: entry.position,
              })
              .eq("id", target.id);
            if (error) throw error;
          }

          const curriculumId = idBySlug.get(entry.slug);
          if (curriculumId === undefined) throw new Error("Kein Lehrgang zum Schreiben");

          const { error: versionError } = await client
            .from("curriculum_versions")
            .insert({
              curriculum_id: curriculumId,
              version: entry.nextVersion,
              content: entry.bundle as unknown as Json,
              notes: entry.notes,
            });
          if (versionError) throw versionError;

          written.push(entry);
          reports.push({
            slug: entry.slug,
            title: entry.title,
            ok: true,
            version: entry.nextVersion,
            message: entry.isNew
              ? `Angelegt, Entwurf als Fassung ${entry.nextVersion}`
              : `Entwurf als Fassung ${entry.nextVersion}`,
          });
        } catch (cause) {
          if (createdId !== null) {
            // Zurücknehmen, was ohne den Entwurf keinen Sinn ergibt.
            await client.from("curricula").delete().eq("id", createdId);
            idBySlug.delete(entry.slug);
          }
          reports.push({
            slug: entry.slug,
            title: entry.title,
            ok: false,
            version: entry.nextVersion,
            message: cause instanceof Error ? cause.message : String(cause),
          });
        }
      }

      // ── Zweiter Durchgang: „baut auf“ ───────────────────────────────────
      for (const entry of written) {
        const requiresId = requiresIdOf(entry, idBySlug);
        if (requiresId === null && entry.requiresSlug !== null) continue;
        const id = idBySlug.get(entry.slug);
        if (id === undefined) continue;
        const { error } = await client
          .from("curricula")
          .update({ requires_id: requiresId })
          .eq("id", id);
        if (error) {
          const report = reports.find((r) => r.slug === entry.slug);
          if (report) {
            reports[reports.indexOf(report)] = {
              ...report,
              message: `${report.message} – „baut auf“ blieb offen: ${error.message}`,
            };
          }
        }
      }

      return reports;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CURRICULA_KEY });
      void queryClient.invalidateQueries({ queryKey: VERSIONS_KEY });
      void queryClient.invalidateQueries({ queryKey: ["transfer", "live"] });
    },
  });
}
